import React, { Suspense, useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Container } from "./ui/Container";
import { Button } from "./ui/Button";
import heroImage from "../assets/Hero_image.png";
import heroImage800 from "../assets/Hero_image_800w.png";
import heroImage1280 from "../assets/Hero_image_1280w.png";
import heroImageWebP from "../assets/Hero_image.webp";
import heroImageWebP800 from "../assets/Hero_image_800w.webp";
import heroImageWebP1280 from "../assets/Hero_image_1280w.webp";
import { trackTryKnoothClick } from "../lib/analytics";

// Lazy-load Three.js-based gradient to keep it out of the initial bundle
const GradientBackground = React.lazy(() => import("./GradientBackground"));

const Hero = () => {
  const { scrollY } = useScroll();
  const [showGradient, setShowGradient] = useState(false);

  // Defer loading Three.js until after the initial paint
  useEffect(() => {
    if ("requestIdleCallback" in window) {
      requestIdleCallback(() => setShowGradient(true));
    } else {
      setTimeout(() => setShowGradient(true), 1500);
    }
  }, []);

  // Animation transforms
  // Initial state: tilted, scaled down, slightly lower
  // End state: straight, scale 1, moved up
  const rotateX = useTransform(scrollY, [0, 200], [3, 0]);
  const scale = useTransform(scrollY, [0, 200], [0.95, 1]);
  const translateY = useTransform(scrollY, [0, 200], [30, 0]);

  return (
    <section className="sticky top-[-632px] md:top-[-670px] z-0 pt-[280px] pb-0">
      {/* Gradient background - deferred until after initial paint */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 0 }}>
        {showGradient && (
          <Suspense fallback={<div className="absolute inset-0 bg-[#FBFBFD]" />}>
            <GradientBackground />
          </Suspense>
        )}
      </div>
      <Container className="relative z-10 text-center mb-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <h1
            className="text-6xl md:text-8xl lg:text-[96px] font-[900] tracking-[-0.02em] mb-8 leading-none text-transparent bg-clip-text pb-2"
            style={{ backgroundImage: "linear-gradient(180deg, #31220D 57%, #666666 100%)" }}
          >
            Your screen.<br />Perfectly recorded.
          </h1>
          <p className="text-xl md:text-[24px] font-normal text-[#666666] max-w-[720px] mx-auto mb-[60px] leading-[1.4] tracking-normal">
            knooth is a macOS screen recording app with built-in editing, AI captions, audio cleanup, and animations. Create tutorials and explainer videos, or record your iPhone and iPad screen - all in one place.
          </p>
          <div className="flex flex-col items-center gap-[10px]">
            <Button
              href="https://apps.apple.com/app/knooth/id6756659300?mt=12"
              target="_blank"
              rel="noreferrer"
              data-analytics="try-knooth"
              data-location="hero"
              onClick={() => trackTryKnoothClick("hero")}
              className="w-[176px] h-[44px] px-6 py-[10px] rounded-full text-white font-bold text-[24px] hover:shadow-lg transition-all border-none leading-none flex items-center justify-center"
              style={{ background: "linear-gradient(180deg, #2F00EC 57%, #C8A5FF 100%)" }}
            >
              Try knooth
            </Button>
            <p className="text-[14px] font-normal leading-[1.4] text-[#31220D]">1 week free for new subscribers.</p>
          </div>
        </motion.div>
      </Container>

      {/* Hero Image Container */}
      <div className="relative w-full max-w-[1600px] mx-auto px-6 perspective-1000">
        <motion.div
          style={{
            rotateX,
            scale,
            y: translateY,
            opacity: 1,
            transformPerspective: 1200,
          }}
          className="relative rounded-2xl"
        >
          <picture>
            <source
              srcSet={`${heroImageWebP800} 800w, ${heroImageWebP1280} 1280w, ${heroImageWebP} 3612w`}
              sizes="(max-width: 768px) 100vw, (max-width: 1400px) 90vw, 1600px"
              type="image/webp"
            />
            <img
              src={heroImage}
              srcSet={`${heroImage800} 800w, ${heroImage1280} 1280w, ${heroImage} 3612w`}
              sizes="(max-width: 768px) 100vw, (max-width: 1400px) 90vw, 1600px"
              alt="knooth App Interface"
              width={3612}
              height={2250}
              fetchpriority="high"
              className="w-full h-auto block rounded-[8px] md:rounded-3xl shadow-[0_10px_40px_20px_rgba(0,0,0,0.08)]"
            />
          </picture>

          {/* Subtle glow effect behind the image */}
          <div className="absolute -inset-px bg-gradient-to-t from-black/5 to-transparent pointer-events-none rounded-2xl" />
        </motion.div>
      </div>

      {/* Full-width fade overlay at bottom - spans entire viewport */}
      <div className="absolute bottom-[-100px] left-0 w-full h-[200px] bg-gradient-to-t from-[#FBFBFD] from-10% via-[#FBFBFD]/60 via-70% to-transparent pointer-events-none z-20" />
    </section>
  );
};

export { Hero };
